import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Filter, ChevronDown, Grid, List } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import ProductCard from '../components/ProductCard';
import { products, categories, getCategoryById } from '../data/products';

const Category = () => {
  const { categoryId } = useParams();
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sortBy, setSortBy] = useState('bestselling');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 100 });
  const [selectedAges, setSelectedAges] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const category = categoryId !== 'all' ? getCategoryById(categoryId) : null;

  const ageGroups = ['1-2', '3-6', '6-12', '8-12', '3-8', '3-10', '3-12'];
  const brands = [...new Set(products.map(p => p.brand))];

  useEffect(() => {
    let result = categoryId === 'all' 
      ? [...products] 
      : products.filter(p => p.category === categoryId);

    // Apply filters
    if (selectedAges.length > 0) {
      result = result.filter(p => selectedAges.includes(p.ageGroup));
    }

    if (selectedBrands.length > 0) {
      result = result.filter(p => selectedBrands.includes(p.brand));
    }

    result = result.filter(p => p.price >= priceRange.min && p.price <= priceRange.max);

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        result.sort((a, b) => b.id - a.id);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'bestselling':
      default:
        result.sort((a, b) => b.reviews - a.reviews);
        break;
    }

    setFilteredProducts(result);
  }, [categoryId, sortBy, priceRange, selectedAges, selectedBrands]);

  const toggleAge = (age) => {
    setSelectedAges(prev => 
      prev.includes(age) ? prev.filter(a => a !== age) : [...prev, age]
    );
  };

  const toggleBrand = (brand) => {
    setSelectedBrands(prev => 
      prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
    );
  };

  const clearFilters = () => {
    setSelectedAges([]);
    setSelectedBrands([]);
    setPriceRange({ min: 0, max: 100 });
    setSortBy('bestselling');
  };

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Price Range */}
      <div>
        <h4 className="font-bold text-slate-800 mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
          Price Range
        </h4>
        <div className="flex items-center gap-2">
          <input
            type="number"
            placeholder="Min"
            value={priceRange.min}
            onChange={(e) => setPriceRange(prev => ({ ...prev, min: Number(e.target.value) }))}
            className="w-20 px-3 py-2 border rounded-lg text-sm"
            data-testid="filter-price-min"
          />
          <span className="text-slate-400">-</span>
          <input
            type="number"
            placeholder="Max"
            value={priceRange.max}
            onChange={(e) => setPriceRange(prev => ({ ...prev, max: Number(e.target.value) }))}
            className="w-20 px-3 py-2 border rounded-lg text-sm"
            data-testid="filter-price-max"
          />
        </div>
      </div>

      {/* Age Group */}
      <div>
        <h4 className="font-bold text-slate-800 mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
          Age Group
        </h4>
        <div className="space-y-2">
          {ageGroups.map((age) => (
            <div key={age} className="flex items-center gap-2">
              <Checkbox
                id={`age-${age}`}
                checked={selectedAges.includes(age)}
                onCheckedChange={() => toggleAge(age)}
                data-testid={`filter-age-${age}`}
              />
              <label 
                htmlFor={`age-${age}`} 
                className="text-sm text-slate-600 cursor-pointer"
              >
                {age} years
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Brands */}
      <div>
        <h4 className="font-bold text-slate-800 mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
          Brand
        </h4>
        <div className="space-y-2">
          {brands.map((brand) => (
            <div key={brand} className="flex items-center gap-2">
              <Checkbox
                id={`brand-${brand}`}
                checked={selectedBrands.includes(brand)}
                onCheckedChange={() => toggleBrand(brand)}
                data-testid={`filter-brand-${brand}`}
              />
              <label 
                htmlFor={`brand-${brand}`} 
                className="text-sm text-slate-600 cursor-pointer"
              >
                {brand}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Clear Filters */}
      <Button
        variant="outline"
        className="w-full rounded-full"
        onClick={clearFilters}
        data-testid="clear-filters-btn"
      >
        Clear All Filters
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Category Banner */}
      <div className="bg-sky-50 py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 
            className="text-3xl md:text-4xl font-bold text-slate-800 mb-2"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
          >
            {category ? category.name : 'All Toys'}
          </h1>
          <p className="text-slate-600" style={{ fontFamily: 'Quicksand, sans-serif' }}>
            {category ? category.description : 'Browse our complete collection of fun and educational toys'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <p className="text-slate-600" style={{ fontFamily: 'Quicksand, sans-serif' }}>
            Showing {filteredProducts.length} products
          </p>

          <div className="flex items-center gap-4 w-full sm:w-auto">
            {/* Mobile Filter Button */}
            <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="outline" className="rounded-full" data-testid="mobile-filter-btn">
                  <Filter size={18} className="mr-2" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] bg-white">
                <SheetHeader>
                  <SheetTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <FilterContent />
                </div>
              </SheetContent>
            </Sheet>

            {/* Sort Dropdown */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] rounded-full" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bestselling">Best Selling</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="rating">Highest Rated</SelectItem>
                <SelectItem value="newest">New Arrivals</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Desktop Sidebar Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-2xl border border-slate-100 p-6">
              <h3 
                className="text-lg font-bold text-slate-800 mb-6"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Filters
              </h3>
              <FilterContent />
            </div>
          </aside>

          {/* Product Grid */}
          <div className="flex-1">
            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-xl text-slate-600 mb-4" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                  No products found matching your filters
                </p>
                <Button 
                  variant="outline" 
                  className="rounded-full"
                  onClick={clearFilters}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Category;
